import parse, { DOMNode, Element } from 'html-react-parser';
import { sanitize } from 'config/xssConfig';
import { Link } from 'react-router-dom';
import UrlPreviewCard from 'ui/UrlPreviewCard';

const IMG_CDN = process.env.REACT_APP_IMAGE_CDN;

// Function to get platform icon (based on UserProfilePlate logic)
export const getLinkIcon = (platform: string, url: string) => {
  if (!platform) return null;

  switch (platform) {
    case 'youtube':
      return (
        <span className={`size-4 flex-shrink-0 rounded-sm`}>
          <img className={`size-4`} src={`${IMG_CDN}/icons/youtube_icon.svg`} alt="YouTube" />
        </span>
      );
    case 'tiktok':
      return (
        <span className={`size-4 flex-shrink-0 rounded-sm`}>
          <img className={`size-4 rounded-full bg-white`} src={`${IMG_CDN}/icons/tiktok_icon_new.svg`} alt="TikTok" />
        </span>
      );
    case 'instagram':
      return (
        <span className={`size-4 flex-shrink-0 rounded-sm`}>
          <img className={`size-4`} src={`${IMG_CDN}/icons/instagram_icon.png`} alt="Instagram" />
        </span>
      );
    case 'x':
      return (
        <span className={`size-4 flex-shrink-0 rounded-sm`}>
          <img className={`size-4 rounded-full bg-white`} src={`${IMG_CDN}/icons/x_icon.png`} alt="X" />
        </span>
      );
    case 'facebook':
      return (
        <span className={`size-4 flex-shrink-0 rounded-sm`}>
          <img className={`size-4`} src={`${IMG_CDN}/icons/facebook_icon.svg`} alt="Facebook" />
        </span>
      );
    case 'reddit':
      return (
        <span className={`size-4 flex-shrink-0 rounded-sm`}>
          <img className={`size-4`} src={`${IMG_CDN}/icons/reddit_icon.svg`} alt="Reddit" />
        </span>
      );
    case 'wikipedia':
      return (
        <span className={`size-4 flex-shrink-0 rounded-sm dark:bg-eoe-bg`}>
          <img className={`size-4`} src={`${IMG_CDN}/icons/wikipedia_icon.svg`} alt="Wikipedia" />
        </span>
      );
    case 'custom':
      try {
        const linkHostname = new URL(url).hostname;
        if (linkHostname) {
          return (
            <span className={`size-4 flex-shrink-0 rounded-sm`}>
              <img 
                className={`size-4 rounded-sm`} 
                src={`https://icons.duckduckgo.com/ip2/${linkHostname}.ico`}
                alt={`${linkHostname} icon`} 
                onError={(e) => {
                  const target = e.currentTarget;
                  target.onerror = null; // Prevent infinite fallback loop
                  
                  // Try fallback services in order
                  const services = [
                    `https://www.google.com/s2/favicons?domain_url=${linkHostname}`,
                    `https://favicon.yandex.net/favicon/${linkHostname}`,
                    `https://f1.allesedv.com/16/${linkHostname}`,
                    `${IMG_CDN}/icons/link_icon.svg`
                  ];
                  
                  let currentIndex = 0;
                  const tryNextService = () => {
                    if (currentIndex < services.length) {
                      target.src = services[currentIndex];
                      currentIndex++;
                      if (currentIndex < services.length) {
                        target.onerror = tryNextService;
                      }
                    }
                  };
                  
                  tryNextService();
                }}
              />
            </span>
          );
        }
      } catch (error) {
        console.error("Error parsing URL:", error);
      }
      // Default icon if URL parsing fails
      return (
        <span className={`size-4`}>
          <img className={`size-4 rounded-sm flex-shrink-0`} src={`${IMG_CDN}/icons/link_icon.svg`} alt="Link" />
        </span>
      );
    default:    
      return null;
  }
};

export interface TransformOptions {
  replaceLinksWithPreviews?: boolean;
  enableMobilePreview?: boolean;
}

export const transformHtmlContent = (htmlContent: string, options?: TransformOptions) => {
  const { enableMobilePreview = true } = options || {};
  
  return parse(sanitize(htmlContent), {
    replace: (domNode: DOMNode) => {
      if (
        domNode.type === 'tag' &&
        (domNode as Element).name === 'span' &&
        (domNode as Element).attribs &&
        (domNode as Element).attribs['data-type']
      ) {
        const element = domNode as Element;
        const dataType = element.attribs['data-type'];
        const className = element.attribs['class'];

        if (dataType === 'mention') {
          const username = element.attribs['data-label'];
          return (
            <Link
              to={`/user/${username}`}
              className={className}
              key={`mention-${username}`}
            >
              @{username}
            </Link>
          );
        } else if (dataType === 'external-mention') {
          const url = element.attribs['data-url'];
          const label = element.attribs['data-label'];
          const platform = element.attribs['data-platform'] || 'custom';
          
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={`${className} inline-flex items-center gap-1`}
              key={`external-mention-${label}-${url}`}
              style={{ textDecoration: 'none' }}
            >
              {getLinkIcon(platform, url)}
              <span className="font-medium text-eoe-text dark:text-eoe-dark-text underline">
                {label}
              </span>
            </a>
          );
        } else if (dataType === 'hashtag') {
          const tag = element.attribs['data-label'];
          const issueId = element.attribs['data-issue-id'];
          const issueName = element.attribs['data-issue-name'];
          const issueCname = element.attribs['data-issue-cname'];
          
          // Check if this is an issue tag
          if (tag && tag.startsWith('issue_tag_')) {
            // Use the data attributes if available, otherwise extract from tag
            const cname = issueCname || tag.replace('issue_tag_', '');
            const displayName = issueName || cname.replace(/_/g, ' ');
            const linkKey = issueId ? `issue-tag-${issueId}` : `issue-tag-${cname}`;
            
            // Add data attribute for mobile preview if enabled
            const linkProps: Record<string, string> = {};
            if (enableMobilePreview) {
              linkProps['data-issue-preview'] = cname;
            }
            
            return (
              <Link
                to={`/issue/${cname}`}
                className={`${className} inline-flex items-center`}
                key={linkKey}
                style={{ textDecoration: 'none' }}
                {...linkProps}
              >
                <span className="inline-flex items-center justify-center size-4 rounded-full text-xs font-medium bg-soc-purple text-white mr-0.5">
                  #
                </span>
                <span className="font-bold text-eoe-text dark:text-eoe-dark-text">
                  {displayName}
                </span>
              </Link>
            );
          } else {
            return (
              <Link
                to={`/tag/${tag}`}
                className={className}
                key={`hashtag-${tag}`}
              >
                #{tag}
              </Link>
            );
          }
        }
      }

      // Optionally replace editor links with preview cards, in-place
      if (
        options?.replaceLinksWithPreviews &&
        domNode.type === 'tag' &&
        (domNode as Element).name === 'a' &&
        (domNode as Element).attribs
      ) {
        const a = domNode as Element;
        const className = a.attribs['class'] || '';
        const href = a.attribs['href'];
        if (className.split(' ').includes('text-editor-link') && href) {
          return (
            <div className="ignore-card-click my-2">
              <UrlPreviewCard url={href} />
            </div>
          );
        }
      }
    },
  });
};
